import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { HeroSection } from "@/components/HeroSection";
import { JobCard } from "@/components/JobCard";
import { SmartJobDisplay } from "@/components/SmartJobDisplay";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, TrendingUp, Award, ChevronRight, LogOut, Briefcase, Home, BarChart3, Settings, Search, Bot, Clock, CreditCard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslations } from "@/translations";
import { SEOHead } from "@/components/seo/SEOHead";
import { StructuredData } from "@/components/seo/StructuredData";
import { JobAlertButton } from "@/components/JobAlertButton";
import { TestimonialsSection } from "@/components/TestimonialsSection";

import { TrustSignals } from "@/components/TrustSignals";


const Index = () => {
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [aiMatchedJobs, setAiMatchedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchJobType, setSearchJobType] = useState('');
  const [isAiSearch, setIsAiSearch] = useState(false);
  const { user, signOut } = useAuth();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const navigate = useNavigate();

  // Mock featured jobs data for demonstration
  const mockJobs = [
    {
      id: "1",
      title: "Senior Software Engineer",
      companies: { name: "TechFlow Solutions", logo_url: null },
      location: "Colombo",
      job_type: "Full-time",
      experience_level: "Senior",
      salary_min: 200000,
      salary_max: 350000,
      currency: "LKR",
      is_remote: true,
      featured: true,
      created_at: new Date().toISOString()
    },
    {
      id: "2", 
      title: "Digital Marketing Manager",
      companies: { name: "Growth Labs", logo_url: null },
      location: "Kandy",
      job_type: "Full-time", 
      experience_level: "Mid-level",
      salary_min: 150000,
      salary_max: 250000,
      currency: "LKR",
      is_remote: false,
      featured: true,
      created_at: new Date().toISOString()
    },
    {
      id: "3",
      title: "UI/UX Designer", 
      companies: { name: "Creative Studio", logo_url: null },
      location: "Galle",
      job_type: "Contract",
      experience_level: "Mid-level", 
      salary_min: 100000,
      salary_max: 180000,
      currency: "LKR",
      is_remote: true,
      featured: true,
      created_at: new Date().toISOString()
    }
  ];

  const handleSearch = async (query: string, location: string, jobType: string) => {
    setSearchLoading(true);
    setIsAiSearch(false);
    setSearchQuery(query);
    setSearchLocation(location);
    setSearchJobType(jobType);
    
    try {
      let queryBuilder = supabase
        .from('jobs')
        .select(`
          *,
          companies (
            name,
            logo_url
          )
        `)
        .eq('status', 'active');

      if (query) {
        queryBuilder = queryBuilder.or(`title.ilike.%${query}%,description.ilike.%${query}%,required_skills.cs.{${query}}`);
      }
      
      if (location && location !== 'all') {
        queryBuilder = queryBuilder.eq('location', location);
      }
      
      if (jobType && jobType !== 'all') {
        queryBuilder = queryBuilder.eq('job_type', jobType);
      }

      const { data, error } = await queryBuilder
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error searching jobs:', error);
        setSearchResults([]);
      } else {
        setSearchResults(data || []);
      }
    } catch (error) {
      console.error('Error in search:', error);
      setSearchResults([]);
    }
    
    setSearchLoading(false);
    setShowResults(true);
    
    // Scroll to results section
    setTimeout(() => {
      const resultsSection = document.getElementById('search-results');
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleAIMatch = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    setAiLoading(true);
    setIsAiSearch(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-match-jobs', {
        body: { userId: user.id }
      });

      if (error) {
        console.error('Error fetching AI matched jobs:', error);
        setAiMatchedJobs([]);
      } else {
        setAiMatchedJobs(data?.jobs || []);
      }
    } catch (error) {
      console.error('Error in AI matching:', error);
      setAiMatchedJobs([]);
    }
    
    setAiLoading(false);
    setShowResults(true);
    
    // Scroll to results section
    setTimeout(() => {
      const resultsSection = document.getElementById('search-results');
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };



  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('jobs')
      .select(`
        *,
        companies (
          name,
          logo_url
        )
      `)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(6);

    if (error) {
      console.error('Error fetching jobs:', error);
    } else {
      setFeaturedJobs(data || []);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title="GreatJobs.lk - Find Your Dream Job in Sri Lanka"
        description="Connect with top employers and discover thousands of opportunities across Sri Lanka. AI-powered job matching, real-time applications, and premium job listings."
        canonical="https://greatjobs.lk/"
        keywords={[
          'jobs sri lanka',
          'sri lanka jobs',
          'career opportunities sri lanka',
          'job board sri lanka',
          'employment sri lanka',
          'job search sri lanka',
          'recruitment sri lanka',
          'vacancies sri lanka'
        ]}
      />
      
      <StructuredData type="WebSite" data={{}} />
      
      <Header />

      {/* Hero Section */}
      <HeroSection onSearch={handleSearch} onAIMatch={handleAIMatch} />


      {/* Search Results Section */}
      {showResults && (
        <section id="search-results" className="py-12 px-4 bg-muted/20">
          <div className="container mx-auto max-w-6xl">
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-2">
                {isAiSearch 
                  ? `${aiMatchedJobs.length} AI matched jobs found`
                  : `${searchResults.length} jobs found`
                }
              </h2>
              <p className="text-muted-foreground mb-4">
                {isAiSearch 
                  ? "Jobs tailored to your profile and preferences"
                  : searchQuery 
                    ? `Results for "${searchQuery}"${searchLocation && searchLocation !== 'all' ? ` in ${searchLocation}` : ''}${searchJobType && searchJobType !== 'all' ? ` (${searchJobType})` : ''}`
                    : "All available positions"
                }
              </p>
              
              {/* Job Alert Button for regular search results */}
              {!isAiSearch && (searchResults.length > 0) && (
                <div className="mb-4">
                  <JobAlertButton
                    searchQuery={searchQuery}
                    filters={{
                      location: searchLocation !== 'all' ? searchLocation : undefined,
                      job_type: searchJobType !== 'all' ? searchJobType : undefined,
                    }}
                    jobCount={searchResults.length}
                  />
                </div>
              )}
            </div>

            {(searchLoading || aiLoading) ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="animate-pulse">
                    <CardHeader>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-muted rounded-lg"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-muted rounded mb-2"></div>
                          <div className="h-3 bg-muted rounded w-2/3"></div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <div className="h-6 bg-muted rounded w-16"></div>
                          <div className="h-6 bg-muted rounded w-20"></div>
                        </div>
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : isAiSearch ? (
              aiMatchedJobs.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {aiMatchedJobs.map((job: any) => (
                    <JobCard
                      key={job.id}
                      job={{
                        ...job,
                        company: job.companies || job.company
                      }}
                      showApplyButton={true}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold mb-2">No matching jobs found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try updating your profile with more skills and preferences.
                  </p>
                  <Button variant="outline" onClick={() => navigate('/account-settings?tab=profile')}>
                    Update Profile
                  </Button>
                </div>
              )
            ) : (
              searchResults.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {searchResults.map((job: any) => (
                    <JobCard
                      key={job.id}
                      job={{
                        ...job,
                        company: job.companies || job.company
                      }}
                      showApplyButton={true}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold mb-2">No jobs found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your search criteria or browse all available positions.
                  </p>
                  <Button variant="outline" onClick={() => navigate('/jobs')}>
                    View All Jobs
                  </Button>
                </div>
              )
            )}
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-16 px-4 bg-muted/30 pb-8">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">{t.features.title}</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t.features.subtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                icon: Building2,
                title: t.features.topCompanies.title,
                description: t.features.topCompanies.description
              },
              {
                icon: Bot,
                title: t.features.aiMatching.title,
                description: t.features.aiMatching.description
              },
              {
                icon: TrendingUp,
                title: t.features.careerGrowth.title,
                description: t.features.careerGrowth.description
              },
              {
                icon: Award,
                title: t.features.qualityVerified.title,
                description: t.features.qualityVerified.description
              }
            ].map((feature, index) => (
              <Card key={index} className="text-center gradient-card shadow-card hover:shadow-primary transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-gradient-primary mx-auto flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Start & Payment Section */}
      <section className="py-6 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="text-center gradient-card shadow-card hover:shadow-primary transition-all duration-300">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-lg bg-gradient-primary mx-auto flex items-center justify-center mb-3">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-lg">{t.quickStart.getStarted}</CardTitle>
              </CardHeader>
              <CardContent className="pt-1">
                <p className="text-muted-foreground">{t.quickStart.getStartedDesc}</p>
              </CardContent>
            </Card>
            
            <Card className="text-center gradient-card shadow-card hover:shadow-primary transition-all duration-300">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-lg bg-gradient-primary mx-auto flex items-center justify-center mb-3">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-lg">{t.quickStart.payment}</CardTitle>
              </CardHeader>
              <CardContent className="pt-1">
                <p className="text-muted-foreground">{t.quickStart.paymentDesc}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Jobs */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold mb-2">{t.featuredJobs.title}</h2>
              <p className="text-muted-foreground">{t.featuredJobs.subtitle}</p>
            </div>
            <Button variant="outline" className="hidden md:flex" onClick={() => navigate('/jobs')}>
              {t.featuredJobs.viewAll}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-muted rounded-lg"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-muted rounded mb-2"></div>
                        <div className="h-3 bg-muted rounded w-2/3"></div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <div className="h-6 bg-muted rounded w-16"></div>
                        <div className="h-6 bg-muted rounded w-20"></div>
                      </div>
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(featuredJobs.length > 0 ? featuredJobs : mockJobs).map((job: any) => (
                <JobCard
                  key={job.id}
                  job={{
                    ...job,
                    company: job.companies || job.company
                  }}
                  showApplyButton={true}
                />
              ))}
            </div>
          )}
          
          {!loading && featuredJobs.length === 0 && mockJobs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t.featuredJobs.noJobs}</p>
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-hero">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t.cta.title}
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {t.cta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="text-base px-8" onClick={() => { navigate('/auth'); window.scrollTo(0, 0); }}>
              {t.cta.createAccount}
            </Button>
            <Button size="lg" variant="outline" className="text-base px-8 bg-white/10 border-white/20 text-white hover:bg-white/20" onClick={() => { navigate(user ? '/post-job' : '/auth'); window.scrollTo(0, 0); }}>
              {t.cta.postFirstJob}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Index;
